/**
 * Mapea `validationStatus` del backend a la `variant` del `StatusBadge` V2.
 * Variantes válidas: success | warning | error | info | neutral
 *
 * Tabla de equivalencias:
 *   ACTIVE         -> success
 *   PROSPECTO      -> info
 *   INACTIVO       -> neutral
 *   EXPIRED        -> warning
 *   DELETED        -> error
 *   APROBADO       -> info
 *   REPROBADO      -> error
 *   BAJA_TEMPORAL  -> info
 *   DESARROLLADOR  -> info
 *   default        -> neutral
 */
export const SUBMEMBER_STATUS_VARIANTS = {
  ACTIVE: "success",
  PROSPECTO: "info",
  INACTIVO: "neutral",
  EXPIRED: "warning",
  DELETED: "error",
  APPROVED: "info",
  FAILED: "error",
  DESARROLLADOR: "info",
  TEMPORALLY_LEAVE: "info",
};

/**
 * Etiquetas legibles en español para mostrar en la UI.
 */
export const SUBMEMBER_STATUS_LABELS = {
  ACTIVE: "Activo",
  PROSPECTO: "Prospecto",
  INACTIVO: "Inactivo",
  EXPIRED: "Expirado",
  DELETED: "Eliminado",
  APPROVED: "Aprobado",
  FAILED: "Reprobado",
  DESARROLLADOR: "Desarrollador",
  TEMPORALLY_LEAVE: "Baja temporal",
};

export const getStatusVariant = (status) =>
  SUBMEMBER_STATUS_VARIANTS[status] || "neutral";

export const getStatusLabel = (status) =>
  SUBMEMBER_STATUS_LABELS[status] || status || "Desconocido";

/**
 * Devuelve la inicial del nombre + apellido para avatar placeholder.
 */
export const getAffiliateInitials = (affiliate) => {
  if (!affiliate) return "?";
  const first = (affiliate.name || "").trim().charAt(0);
  const last = (affiliate.firstLastname || "").trim().charAt(0);
  return ((first + last) || "?").toUpperCase();
};

export const getAffiliateFullName = (affiliate) => {
  if (!affiliate) return "Sin nombre";
  const first = (affiliate.name || "").trim();
  const last = (affiliate.firstLastname || "").trim();
  const joined = `${first} ${last}`.trim();
  return joined || "Sin nombre";
};

export const formatAffiliateDate = (value) => {
  if (!value) return "—";
  try {
    const m = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(m.getTime())) return "—";
    const day = String(m.getDate()).padStart(2, "0");
    const month = String(m.getMonth() + 1).padStart(2, "0");
    const year = m.getFullYear();
    return `${day}/${month}/${year}`;
  } catch (e) {
    return "—";
  }
};

export const isAffiliateActive = (affiliate) =>
  Boolean(affiliate) && affiliate.validationStatus === "ACTIVE";

export const isAffiliatePending = (affiliate) =>
  Boolean(affiliate) && affiliate.validationStatus === "PROSPECTO";

/**
 * Formatea el valor de un campo dinámico para mostrar en UI (strings legibles).
 */
export const formatFieldValue = (value) => {
  if (value === null || value === undefined || value === "") return "—";
  if (Array.isArray(value)) {
    const cleaned = value.filter(
      (v) => v !== null && v !== undefined && v !== ""
    );
    return cleaned.length ? cleaned.join(", ") : "—";
  }
  if (typeof value === "boolean") return value ? "Sí" : "No";
  return String(value);
};

/**
 * Construye los grupos de campos de un submiembro listos para renderizar
 * en la vista expandida. Usa el orden y las secciones del org.
 *
 * @param {Object} affiliate
 * @param {Array} memberFields  - Definiciones vivas de campos del org
 * @param {Array} formSections  - Secciones configuradas por el org
 * @param {Function} groupFn    - groupFieldsBySection de @/utils
 * @param {Function} enrichFn   - enrichMemberDataWithDefs de @/utils
 * @param {Function} sortFn     - sortFieldsByOrder de @/utils
 * @returns {Array} groups [{ _id, title, fields: [{ formattedName, name, value, fieldType }] }]
 */
export const buildAffiliateFieldGroups = (
  affiliate,
  memberFields,
  formSections,
  groupFn,
  enrichFn,
  sortFn
) => {
  if (!affiliate || typeof groupFn !== "function") return [];
  const memberData = Array.isArray(affiliate.memberData)
    ? affiliate.memberData
    : [];
  const enriched = enrichFn
    ? enrichFn(memberData, memberFields).filter((f) => f.fieldType !== "DOC")
    : memberData;
  const sorted = sortFn ? sortFn(enriched) : enriched;
  const groups = groupFn(formSections || [], sorted);
  return (groups || []).filter(
    (group) => group.fields && group.fields.length > 0
  );
};
